// Sidebar.jsx
import React, { useMemo, useContext, useState, useEffect, useCallback } from 'react';
import { Layout, Menu, theme, Drawer, Avatar, Button, Dropdown, Tooltip } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../../index';
import {
  MdDashboard,
  MdSettings,
  MdLogout,
  MdLanguage,
  MdDarkMode,
  MdLightMode
} from 'react-icons/md';
import { UserOutlined } from '@ant-design/icons';
import { TbChartCohort } from "react-icons/tb";
import { BsFillFileSpreadsheetFill } from "react-icons/bs";
import { FaChartPie, FaVault } from "react-icons/fa6";
import { FaUsers } from "react-icons/fa";
import PolicySidebarButton from '../../common/PolicySidebarButton';
import { getSidebarPolicies, EMPTY_NAV_POLICY_CONFIG } from '../../../utils/navPolicyConfig';
import { performSessionLogout } from '../../../utils/sessionLogout';
import { PUBLIC_HEADER_HEIGHT } from './PublicHeader';


const { Sider } = Layout;

const ArrowIcon = ({ collapsed, color }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" 
    style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
    <path d="M10 12L6 8L10 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Sidebar = ({
  collapsed = false,
  userRole,
  algorithm,
  isMobile,
  visible,
  onClose,
  onToggleCollapse,
  navPolicies = EMPTY_NAV_POLICY_CONFIG,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toggleTheme, changeLocale } = useContext(ThemeContext);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const userInfo = useMemo(() => JSON.parse(localStorage.getItem('userInfo')), []);
  
  const {
    token: { colorBgContainer, colorText, colorPrimary, colorBorder, colorBgElevated, colorTextSecondary },
  } = theme.useToken();

  const languageOptions = useMemo(() => [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
  ], []);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  const handleLanguageChange = useCallback((value) => {
    changeLocale(value);
    setCurrentLanguage(value);
    i18n.changeLanguage(value);
  }, [changeLocale, i18n]);

  const handleLogout = () => {
    performSessionLogout();
  };

  const menuItems = useMemo(() => {
    // Permission is pulled from navPolicies (persisted) — falls back to null (open)
    const perm = (key) => getSidebarPolicies(navPolicies, key);

    const items = [
      {
        key: 'dashboard',
        icon: <MdDashboard size={20} />,
        label: t('Overview'),
        permission: perm('dashboard'),
      },
      {
        key: 'upload_batches',
        icon: <BsFillFileSpreadsheetFill size={20} />,
        label: t('Upload Batches'),
        permission: perm('upload_batches'),
      },
      {
        key: 'patients',
        icon: <FaUsers size={20} />,
        label: t('Patients'),
        permission: perm('patients'),
      },
      {
        key: 'cohorts',
        icon: <TbChartCohort size={20} />,
        label: t('Cohorts'),
        permission: perm('cohorts'),
      },
     
      {
        key: 'reporting',
        icon: <FaChartPie size={20} />,
        label: t('Reporting'),
        permission: perm('reporting'),
      },
      {
        key: 'identifier_vault',
        icon: <FaVault size={20} />,
        label: t('Identifier Vault'),
        permission: perm('identifier_vault'),
      },
      {
        key: 'settings',
        icon: <MdSettings size={20} />,
        label: t('Settings'),
        permission: null,
      },
    ];
    // Hide settings if not SuperUser
    const roles = userInfo?.roles;
    if (!roles || !Array.isArray(roles) || !roles.includes('SuperUser')) {
      return items.filter(item => item.key !== 'settings');
    }
    return items;
  }, [navPolicies, userRole, userInfo, t]);

  const handleMenuClick = ({ key }) => {
    navigate(`/${key}`);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const selectedKey = useMemo(() => {
    return location.pathname.split('/')[1] || 'dashboard';
  }, [location.pathname]);

  const sidebarContent = (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%'
    }}>
      {!isMobile && (
        <div style={{
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 12px',
          paddingTop: '12px'
        }}>
          <Button
            type="text"
            onClick={onToggleCollapse}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colorBgElevated;
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.opacity = '0.6';
            }}
            style={{
              width: '28px',
              height: '28px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${colorBorder}`,
              borderRadius: '6px',
              transition: 'all 0.2s',
              backgroundColor: 'transparent',
              color: colorText,
              opacity: 0.6
            }}
          >
            <ArrowIcon collapsed={collapsed} color={colorText} />
          </Button>
        </div>
      )}

      <div style={{ flex: 1 }}>
        <div style={{ 
          padding: collapsed ? '16px 4px' : (isMobile ? '12px 8px' : '16px 8px'),
          display: 'flex',
          flexDirection: 'column',
          gap: collapsed ? '8px' : '12px'
        }}>
          {menuItems.map(item => (
            <PolicySidebarButton
              key={item.key}
              permission={item.permission}
              requireAll={item.requireAll !== undefined ? item.requireAll : (Array.isArray(item.permission) ? false : false)}
              showTooltip={!isMobile && collapsed}
              tooltipTitle={item.label}
            >
              <button
                onClick={() => handleMenuClick({ key: item.key })}
                style={{
                  display: 'flex',
                  flexDirection: collapsed ? 'row' : 'column',
                  alignItems: 'center',
                  justifyContent: collapsed ? 'center' : 'center',
                  gap: collapsed ? '0' : '8px',
                  padding: collapsed ? '8px 4px' : '16px 8px',
                  background: selectedKey === item.key ? colorPrimary : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  minHeight: collapsed ? 'auto' : '80px',
                  color: selectedKey === item.key ? '#fff' : colorText,
                }}
                onMouseEnter={(e) => {
                  if (selectedKey !== item.key) {
                    e.currentTarget.style.background = colorBorder;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedKey !== item.key) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {React.cloneElement(item.icon, { size: collapsed ? 20 : 24 })}
                {!collapsed && (
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    textAlign: 'center',
                    lineHeight: '1.2',
                    wordBreak: 'break-word'
                  }}>
                    {item.label}
                  </span>
                )}
              </button>
            </PolicySidebarButton>
          ))}
        </div>
      </div>

      {/* User Profile Section */}
      <div style={{
        padding: collapsed ? '12px 4px' : '16px',
        borderTop: `1px solid ${colorBorder}`,
        display: 'flex',
        flexDirection: 'column',
        gap: collapsed ? '8px' : '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? '4px' : '12px',
          padding: '4px',
          justifyContent: collapsed ? 'center' : 'flex-start'
        }}>
          <Avatar
            size={collapsed ? 24 : 32}
            style={{
              backgroundColor: colorPrimary,
              flexShrink: 0,
              cursor: 'pointer'
            }}
            icon={<UserOutlined style={{ fontSize: collapsed ? '14px' : '16px' }} />}
            onClick={() => navigate('/myprofile')}
          />
          {!collapsed && (
            <div style={{
              flex: 1,
              overflow: 'hidden'
            }}>
              <div style={{ 
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {userInfo?.username || 'User'}
              </div>
              <div style={{ 
                fontSize: '12px',
                color: colorText,
                opacity: 0.7,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {userInfo?.businessOrganisationName || 'Organization'}
              </div>
            </div>
          )}
        </div>
        
        {!collapsed ? (
          <>
     
            <Button
              type="text"
              icon={algorithm === theme.darkAlgorithm ? <MdLightMode size={20} /> : <MdDarkMode size={20} />}
              onClick={toggleTheme}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 8px'
              }}
            >
              {algorithm === theme.darkAlgorithm ? 'Light' : 'Dark'}
            </Button>

            <Dropdown
              menu={{
                items: languageOptions.map(option => ({
                  key: option.value,
                  label: option.label
                })),
                onClick: ({ key }) => handleLanguageChange(key)
              }}
              trigger={['click']}
              placement="right"
            >
              <Button
                type="text"
                icon={<MdLanguage size={20} />}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 8px'
                }}
              >
                {languageOptions.find(option => option.value === currentLanguage)?.label || 'Language'}
              </Button>
            </Dropdown>

            <Button
              type="text"
              icon={<MdLogout size={20} />}
              onClick={handleLogout}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 8px'
              }}
            >
              Logout
            </Button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
      
            <Button
              type="text"
              icon={algorithm === theme.darkAlgorithm ? <MdLightMode size={20} /> : <MdDarkMode size={20} />}
              onClick={toggleTheme}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            />
            
            <Dropdown
              menu={{
                items: languageOptions.map(option => ({
                  key: option.value,
                  label: option.label
                })),
                onClick: ({ key }) => handleLanguageChange(key)
              }}
              trigger={['click']}
              placement="right"
            >
              <Button
                type="text"
                icon={<MdLanguage size={20} />}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              />
            </Dropdown>

            <Button
              type="text"
              icon={<MdLogout size={20} />}
              onClick={handleLogout}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        onClose={onClose}
        open={visible}
        closable={false}
        width={160}
        bodyStyle={{ 
          padding: 0, 
          backgroundColor: colorBgContainer
        }}
        style={{
          transition: 'transform 0.3s ease',
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              padding: '12px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {menuItems.map(item => (
                <PolicySidebarButton
                  key={item.key}
                  permission={item.permission}
                  requireAll={item.requireAll !== undefined ? item.requireAll : (Array.isArray(item.permission) ? false : false)}
                  showTooltip={false}
                >
                  <button
                    onClick={() => handleMenuClick({ key: item.key })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      background: selectedKey === item.key ? colorPrimary : 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      width: '100%',
                      color: selectedKey === item.key ? '#FFFFFF' : colorText,
                    }}
                    onMouseEnter={(e) => {
                      if (selectedKey !== item.key) {
                        e.currentTarget.style.background = colorBgElevated;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedKey !== item.key) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {React.cloneElement(item.icon, { size: 16 })}
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {item.label}
                    </span>
                  </button>
                </PolicySidebarButton>
              ))}
            </div>
          </div>

          {/* User Profile Section */}
          <div style={{
            padding: '12px 8px',
            borderTop: `1px solid ${colorBorder}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px'
            }}>
              <Avatar
                size={24}
                style={{
                  backgroundColor: colorPrimary,
                  flexShrink: 0,
                  cursor: 'pointer'
                }}
                icon={<UserOutlined style={{ fontSize: '14px' }} />}
                onClick={() => navigate('/myprofile')}
              />
              <div style={{
                flex: 1,
                overflow: 'hidden'
              }}>
                <div style={{ 
                  fontWeight: 500,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {userInfo?.username || 'User'}
                </div>
                <div style={{ 
                  fontSize: '11px',
                  color: colorText,
                  opacity: 0.7,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {userInfo?.businessOrganisationName || 'Organization'}
                </div>
              </div>
            </div>

            <Button
              type="text"
              icon={algorithm === theme.darkAlgorithm ? <MdLightMode size={14} /> : <MdDarkMode size={14} />}
              onClick={toggleTheme}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 8px',
                fontSize: '13px'
              }}
            >
              {algorithm === theme.darkAlgorithm ? 'Light' : 'Dark'}
            </Button>

            <Dropdown
              menu={{
                items: languageOptions.map(option => ({
                  key: option.value,
                  label: option.label
                })),
                onClick: ({ key }) => handleLanguageChange(key)
              }}
              trigger={['click']}
              placement="right"
            >
              <Button
                type="text"
                icon={<MdLanguage size={14} />}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 8px',
                  fontSize: '13px'
                }}
              >
                {languageOptions.find(option => option.value === currentLanguage)?.label || 'Language'}
              </Button>
            </Dropdown>

            <Button
              type="text"
              icon={<MdLogout size={14} />}
              onClick={handleLogout}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 8px',
                fontSize: '13px'
              }}
            >
              Logout
            </Button>
          </div>
        </div>
      </Drawer>
    );
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={48}
      width={130}
      style={{
        overflow: 'auto',
        height: `calc(100vh - ${PUBLIC_HEADER_HEIGHT}px)`,
        position: 'fixed',
        top: PUBLIC_HEADER_HEIGHT,
        left: 0,
        zIndex: 10,
        backgroundColor: colorBgContainer,
        color: colorText,
        borderRight: `1px solid ${colorBorder}`,
        transition: 'all 0.2s'
      }}
    >
      {sidebarContent}
    </Sider>
  );
}

export default Sidebar;